(() => {
  const revokePreview = (preview) => {
    const image = preview?.querySelector('img');
    if (!image) return;
    if (image.dataset.objectUrl) {
      URL.revokeObjectURL(image.dataset.objectUrl);
      delete image.dataset.objectUrl;
    }
    image.removeAttribute('src');
    preview.classList.remove('has-image');
  };

  const setupCarousel = (root) => {
    const track = root.querySelector('[data-ag-custom-art-track]');
    const slides = [...root.querySelectorAll('[data-ag-custom-art-slide]')];
    const dots = [...root.querySelectorAll('[data-ag-custom-art-dot]')];
    const previous = root.querySelector('[data-ag-custom-art-prev]');
    const next = root.querySelector('[data-ag-custom-art-next]');

    if (!track || !slides.length) return;

    const setActive = (index) => {
      dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === index)));
    };

    const activeIndex = () => {
      const trackCenter = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Number.POSITIVE_INFINITY;
      slides.forEach((slide, index) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const nextDistance = Math.abs(trackCenter - slideCenter);
        if (nextDistance < distance) {
          distance = nextDistance;
          closest = index;
        }
      });
      return closest;
    };

    const moveTo = (index) => {
      const target = slides[Math.max(0, Math.min(index, slides.length - 1))];
      if (!target) return;
      track.scrollTo({
        left: target.offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
    };

    dots.forEach((dot, index) => dot.addEventListener('click', () => moveTo(index)));
    previous?.addEventListener('click', () => moveTo(activeIndex() - 1));
    next?.addEventListener('click', () => moveTo(activeIndex() + 1));
    track.addEventListener('scroll', () => window.requestAnimationFrame(() => setActive(activeIndex())), { passive: true });
    setActive(0);
  };

  const interpolate = (template, value) => template.replace(/\{\{\s*percent\s*\}\}/g, String(value));

  const setupPhotoSubmission = (root) => {
    const productForm = root.querySelector('product-form');
    const form = root.querySelector('form[data-type="add-to-cart-form"]');
    const choices = [...root.querySelectorAll('[data-ag-custom-art-choice]')];
    const method = root.querySelector('[data-ag-custom-art-method]');
    const status = root.querySelector('[data-ag-custom-art-status]');
    const fileInput = root.querySelector('[data-ag-custom-art-file]');
    const fileLabel = root.querySelector('[data-ag-custom-art-file-label]');
    const uploadPanel = root.querySelector('[data-ag-custom-art-upload-panel]');
    const selected = root.querySelector('[data-ag-custom-art-upload-selected]');
    const preview = root.querySelector('[data-ag-custom-art-preview]');
    const filename = root.querySelector('[data-ag-custom-art-filename]');
    const removeButton = root.querySelector('[data-ag-custom-art-remove]');
    const error = root.querySelector('[data-ag-custom-art-upload-error]');
    const laterHelper = root.querySelector('[data-ag-custom-art-later-helper]');
    const switchNotice = root.querySelector('[data-ag-custom-art-switch-notice]');
    const progress = root.querySelector('[data-ag-custom-art-progress]');
    const progressBar = root.querySelector('[data-ag-custom-art-progress-bar]');
    const progressLabel = root.querySelector('[data-ag-custom-art-progress-label]');
    const submit = root.querySelector('[data-ag-custom-art-submit]');
    const submitLabel = root.querySelector('[data-ag-custom-art-submit-label]');
    const uploadNowEnabled = root.dataset.enableUploadNow === 'true';

    if (!uploadNowEnabled) {
      if (!form || !submit || !submitLabel) return;

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        const cart = document.querySelector('cart-drawer');
        const formData = new FormData(form);
        if (cart && typeof cart.getSectionsToRender === 'function') {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        submit.disabled = true;
        submit.setAttribute('aria-disabled', 'true');
        submit.classList.add('loading');

        try {
          const response = await fetch(window.routes?.cart_add_url || '/cart/add.js', {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              Accept: 'application/javascript',
            },
            body: formData,
          });
          const data = await response.json();
          if (!response.ok || data.status) throw new Error(data.description || data.message || 'Cart add failed');
          if (cart && typeof cart.renderContents === 'function') {
            await cart.renderContents(data);
          } else {
            window.location = window.routes?.cart_url || '/cart';
          }
        } catch {
          window.location = window.routes?.cart_url || '/cart';
        } finally {
          submit.disabled = false;
          submit.removeAttribute('aria-disabled');
          submit.classList.remove('loading');
        }
      }, true);

      return;
    }

    if (!form || !choices.length || !method || !status || !fileInput || !uploadPanel || !selected || !preview || !filename || !error || !submit || !submitLabel) return;

    let submitting = false;

    const selectedChoice = () => choices.find((choice) => choice.checked)?.value || 'upload_now';

    const clearError = () => {
      error.textContent = '';
      error.hidden = true;
    };

    const showError = (message) => {
      error.textContent = message || '';
      error.hidden = !message;
      if (message) error.focus();
    };

    const setSubmitLabel = (value) => {
      if (value === 'send_later') {
        submitLabel.textContent = submit.dataset.labelLater || '';
      } else if (fileInput.files?.length) {
        submitLabel.textContent = submit.dataset.labelSelected || '';
      } else {
        submitLabel.textContent = submit.dataset.labelDefault || '';
      }
    };

    const setProgress = (message, percent = null, visible = true, showNote = true) => {
      if (!progress || !progressLabel) return;
      progress.hidden = !visible;
      progress.classList.toggle('is-preparing', percent === null && visible);
      progress.dataset.state = percent === null && visible ? 'preparing' : 'uploading';
      if (!visible) return;
      progress.dataset.indeterminate = percent === null ? 'true' : 'false';
      if (progressBar) progressBar.style.width = percent === null ? '0%' : `${percent}%`;
      if (percent === null) {
        progress.removeAttribute('aria-valuenow');
      } else {
        progress.setAttribute('aria-valuenow', String(percent));
      }
      progressLabel.textContent = `${message}${root.dataset.uploadNote && percent === null && showNote ? ` ${root.dataset.uploadNote}` : ''}`;
    };

    const clearProgress = () => {
      if (!progress) return;
      progress.hidden = true;
      progress.dataset.indeterminate = 'false';
      progress.classList.remove('is-preparing');
      delete progress.dataset.state;
      if (progressBar) progressBar.style.width = '0%';
      progress.removeAttribute('aria-valuenow');
      if (progressLabel) progressLabel.textContent = '';
    };

    const clearSelection = () => {
      fileInput.value = '';
      filename.textContent = '';
      selected.hidden = true;
      revokePreview(preview);
      if (fileLabel) fileLabel.textContent = fileLabel.dataset.defaultLabel;
      clearProgress();
      setSubmitLabel('upload_now');
    };

    const setMethod = (value) => {
      const uploadNow = value === 'upload_now';
      const hadFile = Boolean(fileInput.files?.length);
      method.value = value;
      status.value = uploadNow ? 'uploaded_with_order' : 'pending_after_purchase';
      fileInput.disabled = !uploadNow;
      uploadPanel.hidden = !uploadNow;
      if (laterHelper) laterHelper.hidden = uploadNow;
      if (switchNotice) {
        switchNotice.textContent = root.dataset.switchLater || '';
        switchNotice.hidden = uploadNow || !hadFile;
      }

      if (!uploadNow) clearSelection();
      if (fileLabel && uploadNow) {
        fileLabel.textContent = fileInput.files?.length ? fileLabel.dataset.changeLabel : fileLabel.dataset.defaultLabel;
      }
      setSubmitLabel(value);
      clearError();
    };

    const setSubmitting = (value) => {
      submitting = value;
      submit.disabled = value;
      submit.setAttribute('aria-disabled', String(value));
      submit.classList.toggle('loading', value);
      submit.querySelector('.loading__spinner')?.classList.toggle('hidden', !value);
    };

    const removePhoto = () => {
      clearSelection();
      if (switchNotice) switchNotice.hidden = true;
      clearError();
    };

    const getCart = () => {
      const candidate = document.querySelector('cart-drawer') || productForm?.cart || document.querySelector('cart-notification');
      if (!candidate || typeof candidate.getSectionsToRender !== 'function' || typeof candidate.renderContents !== 'function') return null;
      return candidate;
    };

    const submitWithProgress = () => {
      const choice = selectedChoice();
      const file = fileInput.files?.[0];
      if (choice === 'upload_now' && !file) {
        showError(root.dataset.photoRequired || '');
        return;
      }
      if (submitting) return;

      const cart = getCart();
      const formData = new FormData(form);
      formData.set('properties[_ag_photo_method]', choice);
      formData.set('properties[_ag_photo_status]', choice === 'upload_now' ? 'uploaded_with_order' : 'pending_after_purchase');
      if (choice === 'send_later') {
        formData.delete('properties[_ag_customer_photo]');
        formData.delete('properties[_ag_photo_original_name]');
      } else if (file) {
        formData.set('properties[_ag_photo_original_name]', file.name);
      }

      if (cart) {
        formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        cart.setActiveElement(document.activeElement);
      }

      clearError();
      setSubmitting(true);
      if (choice === 'send_later') {
        setProgress(root.dataset.uploadPrepare || '', null, true, false);
      } else {
        setProgress(root.dataset.uploadStart || '', 0);
      }

      const xhr = new XMLHttpRequest();
      xhr.open('POST', window.routes?.cart_add_url || '/cart/add.js', true);
      xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
      xhr.setRequestHeader('Accept', 'application/javascript');
      xhr.timeout = 180000;

      xhr.upload.onprogress = (event) => {
        if (choice === 'send_later') return;
        if (event.lengthComputable && event.total > 0) {
          setProgress(interpolate(root.dataset.uploadProgress || '', Math.round((event.loaded / event.total) * 100)), Math.round((event.loaded / event.total) * 100));
        } else {
          setProgress(root.dataset.uploadStart || '', null);
        }
      };

      const finishFailure = (message) => {
        setSubmitting(false);
        clearProgress();
        showError(message || root.dataset.uploadError || '');
      };

      xhr.onload = async () => {
        let response;
        try {
          response = JSON.parse(xhr.responseText || '{}');
        } catch (parseError) {
          finishFailure(root.dataset.uploadError);
          return;
        }

        if (xhr.status < 200 || xhr.status >= 300 || response.status) {
          finishFailure(response.description || response.message || root.dataset.uploadError);
          return;
        }

        setProgress(root.dataset.uploadPrepare || '', null, true, choice !== 'send_later');
        try {
          if (!cart) {
            window.location = window.routes?.cart_url || '/cart';
            return;
          }
          await cart.renderContents(response);
          if (choice === 'send_later') {
            clearProgress();
          } else {
            setProgress(root.dataset.uploadDone || '', 100);
            window.setTimeout(clearProgress, 1200);
          }
          setSubmitting(false);
        } catch {
          setSubmitting(false);
          window.location = window.routes?.cart_url || '/cart';
        }
      };

      xhr.onerror = () => finishFailure(root.dataset.uploadError);
      xhr.ontimeout = () => finishFailure(root.dataset.uploadError);
      xhr.onabort = () => finishFailure(root.dataset.uploadError);
      xhr.send(formData);
    };

    choices.forEach((choice) => choice.addEventListener('change', () => setMethod(selectedChoice())));
    removeButton?.addEventListener('click', removePhoto);

    fileInput.addEventListener('change', () => {
      clearError();
      const file = fileInput.files?.[0];
      if (!file) {
        removePhoto();
        return;
      }

      const upload = choices.find((choice) => choice.value === 'upload_now');
      if (upload) upload.checked = true;
      setMethod('upload_now');
      filename.textContent = file.name;
      selected.hidden = false;
      if (fileLabel) fileLabel.textContent = fileLabel.dataset.changeLabel;
      revokePreview(preview);

      const previewable = file.type.startsWith('image/') && !/heic|heif/i.test(file.type) && !/\.hei[cf]$/i.test(file.name);
      if (previewable) {
        const image = preview.querySelector('img');
        const objectUrl = URL.createObjectURL(file);
        image.src = objectUrl;
        image.dataset.objectUrl = objectUrl;
        preview.classList.add('has-image');
      }
      setSubmitLabel('upload_now');
    });

    form.addEventListener('submit', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      submitWithProgress();
    }, true);

    setMethod(selectedChoice());
  };

  const setupSmoothAnchors = (root) => {
    root.querySelectorAll('a[href="#ag-custom-art-purchase"]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const target = document.querySelector('#ag-custom-art-purchase');
        if (!target) return;
        event.preventDefault();
        target.scrollIntoView({
          behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
          block: 'start',
        });
        window.setTimeout(() => target.querySelector('[data-ag-custom-art-choice]')?.focus({ preventScroll: true }), 350);
      });
    });
  };

  const initialize = () => {
    document.querySelectorAll('[data-ag-custom-art]').forEach((root) => {
      setupCarousel(root);
      setupPhotoSubmission(root);
      setupSmoothAnchors(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }
})();
